import React, { useEffect, useRef, useState } from "react";
import Warnings from "../Warnings";
import StudentInformationForm from "../StudentInformationForm";
import GuardianInformationForm from "../GuardianInformationForm";
import SchoolInfoForm from "../SchoolInfoForm";
import SchoolPaymentInfo from "../SchoolPaymentInfo";
import SupportingDocForm from "../SupportingDocForm";
import CertificationForm from "../CertificationForm";
import useFetch from "../../../../../hooks/useFetch";
import {
  applicationDetails,
  editStepLabel,
  stepList,
} from "../../../../../services/applicationFormService";
import { useLocation } from "react-router-dom";
import useMutation from "../../../../../hooks/useMutation";
import { toast } from "react-toastify";

const ManageApplication = () => {
  const location = useLocation();
  const item = location.state;
  const [currentStepSlug, setCurrentStepSlug] = useState("");
  const [loaded, setLoaded] = useState(false);

  const { data: stepsList, refetch: refetchStepsList } = useFetch({
    dependencies: ["stepsList", item?._id],
    fetchFunction: async () => {
      const res = await stepList({ formId: item?._id });
      return res?.data;
    },
  });

  const [title, setTitle] = useState("");
  const [originalTitle, setOriginalTitle] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  const continueHandler = () => {
    const index = stepsList.findIndex((step) => step.slug === currentStepSlug);
    if (index < stepsList.length - 1) {
      setCurrentStepSlug(stepsList[index + 1].slug);
    }
  };

  const backHandler = () => {
    const index = stepsList.findIndex((step) => step.slug === currentStepSlug);
    if (index > 0) {
      setCurrentStepSlug(stepsList[index - 1].slug);
    }
  };

  useEffect(() => {
    if (!item || !item.steps || !stepsList?.length) return;
    const firstSlug = item.steps?.[0]?.code || stepsList[0]?.slug;
    setCurrentStepSlug(firstSlug);
  }, [item, stepsList]);

  useEffect(() => {
    if (!currentStepSlug || !stepsList) return;
    const step = stepsList.find((s) => s.slug === currentStepSlug);
    if (step) {
      setTitle(step.title);
      setOriginalTitle(step.title);
    }
  }, [currentStepSlug, stepsList]);

  const { data, isLoading } = useFetch({
    dependencies: ["applicationDetailsForm", item?._id, currentStepSlug],
    fetchFunction: async () => {
      setLoaded(true);
      if (!item?._id || !currentStepSlug) return null;

      const res = await applicationDetails({
        formId: item._id,
        groupCode: currentStepSlug,
        userType: "admin",
      });

      return res?.groupCodeWiseData;
    },
    onSuccess: () => setLoaded(false),
    onError: () => setLoaded(false),
  });

  const { mutate } = useMutation({
    mutationFn: async (updatedField) => {
      return await editStepLabel({
        formId: item._id,
        title: updatedField?.title,
        slug: updatedField.slug,
        stepId: updatedField?._id,
      });
    },
    onSuccess: (res) => {
      toast.success(res?.message);
      setTitle(res.data.title);
      setOriginalTitle(res.data.title);
      setIsEditing(false);
      refetchStepsList();
    },
    onError: (error) => {
      console.error("Mutation error:", error);
    },
  });

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    const step = stepsList?.find((s) => s.slug === currentStepSlug);
    if (step && title !== originalTitle) {
      mutate({
        ...step,
        title,
        slug: step.slug,
      });
    } else {
      setIsEditing(false);
    }
  };

  const handleChange = (e) => {
    setTitle(e.target.value);
  };

  const stepComponentsBySlug = {
    Application_Form: (
      <Warnings
        data={data}
        details={item}
        stepsSlug={currentStepSlug}
        continueHandler={continueHandler}
      />
    ),
    Student_Personal_Information: (
      <StudentInformationForm
        data={data}
        details={item}
        stepsSlug={currentStepSlug}
        setLoaded={setLoaded}
        loaded={loaded}
        backHandeler={backHandler}
        continueHandler={continueHandler}
      />
    ),
    Parent_Guardian_Information: (
      <GuardianInformationForm
        data={data}
        details={item}
        stepsSlug={currentStepSlug}
        setLoaded={setLoaded}
        loaded={loaded}
        backHandeler={backHandler}
        continueHandler={continueHandler}
      />
    ),
    School_Information: (
      <SchoolInfoForm
        data={data}
        details={item}
        stepsSlug={currentStepSlug}
        setLoaded={setLoaded}
        loaded={loaded}
        backHandeler={backHandler}
        continueHandler={continueHandler}
      />
    ),
    School_Payment_Information: (
      <SchoolPaymentInfo
        data={data}
        details={item}
        stepsSlug={currentStepSlug}
        setLoaded={setLoaded}
        loaded={loaded}
        backHandeler={backHandler}
        continueHandler={continueHandler}
      />
    ),
    Supporting_Documents: (
      <SupportingDocForm
        data={data}
        details={item}
        stepsSlug={currentStepSlug}
        setLoaded={setLoaded}
        loaded={loaded}
        backHandeler={backHandler}
        continueHandler={continueHandler}
      />
    ),
    Certification_and_Authorization: (
      <CertificationForm
        data={data}
        details={item}
        stepsSlug={currentStepSlug}
        setLoaded={setLoaded}
        loaded={loaded}
        backHandeler={backHandler}
      />
    ),
  };

  return (
    <div>
      <h2
        className="text-center text-3xl font-bold mb-6"
        onDoubleClick={handleDoubleClick}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            className="text-center w-80 border-b-2 border-gray-300 outline-none focus:border-chestnut-rose focus:ring-0"
            value={title}
            onChange={handleChange}
            onBlur={handleBlur}
            autoFocus
            style={{ minWidth: "2rem" }}
          />
        ) : (
          title
        )}
      </h2>

      {isLoading || !currentStepSlug ? (
        <div>Loading...</div>
      ) : (
        stepComponentsBySlug[currentStepSlug] || <div>Step Not Found</div>
      )}
    </div>
  );
};

export default ManageApplication;
